import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import './McpScreen.less';
import Meta from '../../components/Meta';
import {
  convertSubscriptionDataToStripeSubscription,
  getRoleName,
} from '../../services/subscription';
import { Tabs, TabsProps } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import PlanInfo from '../../components/PlanInfo/PlanInfo';
import OrgsTab from './OrgsTab';
import UsersTab from './UsersTab';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { getCreditDisplayText } from '../../utils/creditUtils';
import CreditButton from '../../components/CreditButton/CreditButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserAccess } from '../../hooks/useUserAccess';
import { getTeamName } from '../../services/account.service';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import AiClientConnection from '../../components/AiClientConnection/AiClientConnection';
import ComponentErrorBoundary from '../../components/ErrorBoundary/ComponentErrorBoundary';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';

const _TAB_ORGS = 'orgs';
const _TAB_USERS = 'users';
const _TAB_DEFAULT = _TAB_ORGS;

const McpScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { allTeams: allTeamsInfo, currentTeam: currentTeamInfo } = useSelector(
    (state: RootState) => state.team
  );

  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  // const [license, setLicense] = useState<OrganizationLicense | null>(null);
  const { subscriptionInfo, loading: subscriptionLoading } = useSelector(
    (state: RootState) => state.subscription
  );
  const subscription = subscriptionInfo?.subscription;

  const { currentAccount } = useUserAccess();
  const balanceInfo = useCreditBalance();
  const { isLoading: isLoadingCredits } = balanceInfo;

  // useEffect(() => {
  //   setLicense(getLicense());
  // }, []);

  useEffect(() => {
    setIsLoadingSubscriptions(false);
  }, []);

  // Note: Subscription and credit data is fetched by selectAccount action
  // No need to fetch here since team switching properly handles data refresh

  useEffect(() => {
    console.log('Refresh page after orgs changed', allTeamsInfo.records);
  }, [allTeamsInfo.records]);

  const currentTeam = currentTeamInfo?.record;
  const { hasAdminAccess, isTeamUser } = useUserAccess();
  const { currentAbility } = useAbility();
  const canManageSubscription = currentAbility.can(
    'manage',
    Resource.Subscription,
    convertSubscriptionDataToStripeSubscription(subscription)
  );

  const [activeTab, setActiveTab] = useState<string>(_TAB_DEFAULT);

  const tabs = useMemo(() => {
    return [
      {
        key: _TAB_ORGS,
        label: 'Salesforce Organizations',
        children: (
          <ComponentErrorBoundary componentName="Salesforce Organizations">
            <OrgsTab key={currentTeam?.id || 'personal'} />
          </ComponentErrorBoundary>
        ),
      },
      isTeamUser && {
        key: _TAB_USERS,
        label: 'Users',
        children: (
          <ComponentErrorBoundary componentName="Users Management">
            <UsersTab key={currentTeam?.id || 'personal'} />
          </ComponentErrorBoundary>
        ),
      },
    ].filter(Boolean) as TabsProps['items'];
  }, [currentTeam, hasAdminAccess, isTeamUser]);

  useEffect(() => {
    const tabTag = location.hash.substring(1).toLowerCase();
    const matchingTab = (tabs || []).find((tab) => tab.key === tabTag);
    setActiveTab(matchingTab?.key || _TAB_DEFAULT);
  }, [location.hash, tabs]);

  return (
    <>
      <Meta name="Cirra AI - MCP Management" />
      <div
        id="mcp-management-container"
        className="mcp-management-container"
        data-testid="mcp-management-container"
      >
        <div className="section section-subscriptions">
          <div className="section-title">
            Cirra AI Subscription
            {currentTeam?.master_organization && (
              <span className="parent-team-name">
                (managed by parent team{' '}
                {getTeamName(currentTeam.master_organization)}
                &nbsp;
                <InfoTooltip content="Navigate to the parent team to manage the subscription" />
                )
              </span>
            )}
          </div>
          {!isLoadingSubscriptions && (
            <div className="account-info">
              <div className="account-avatar-container">
                <div className="details">
                  {/* {license && (
                      <div>
                        License:{' '}
                        <span
                          className={`license-info ${
                            license === OrganizationLicense.Guest
                              ? 'license-guest'
                              : license === OrganizationLicense.Full
                                ? 'license-full'
                                : ''
                          }`}
                        >
                          {license}
                        </span>{' '}
                        <Tooltip
                          title={
                            license === OrganizationLicense.Guest
                              ? GUEST_LICENSE
                              : FULL_LICENSE
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>{' '}
                      </div>
                    )} */}
                  {getRoleName() && <div>Role: {getRoleName()}</div>}

                  <div className="subscription-info">
                    <span>
                      {currentTeam?.master_organization
                        ? 'Parent Team Subscription: '
                        : 'Subscription: '}
                    </span>
                    &nbsp;
                    {subscriptionLoading ? (
                      <span>Loading...</span>
                    ) : subscription ? (
                      <ComponentErrorBoundary componentName="Subscription Plan Info">
                        <PlanInfo
                          subscription={subscription}
                          upgradeable
                          portalOpenable={canManageSubscription}
                        />
                      </ComponentErrorBoundary>
                    ) : (
                      <span>No subscription data</span>
                    )}
                  </div>

                  <div className="credits-info">
                    <span>Credits used: </span>
                    {isLoadingCredits ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        <span
                          className={classNames({
                            'credits-count': true,
                            warn: balanceInfo.isRunningOutOfCredits,
                          })}
                        >
                          {getCreditDisplayText(currentAccount, balanceInfo, {
                            withRenewalDate: true,
                          })}{' '}
                        </span>
                        <ComponentErrorBoundary componentName="Credit Button">
                          <CreditButton style={{ display: 'inline-block' }} />
                        </ComponentErrorBoundary>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="separator" />

        <ComponentErrorBoundary componentName="AI Client Connection">
          <AiClientConnection />
        </ComponentErrorBoundary>

        <div id="mcp-doc-and-support" data-testid="mcp-doc-and-support">
          <div className="section-title">Documentation and Support</div>
          <p>
            Learn about the Cirra AI Admin MCP server{' '}
            <a
              href="https://docs.cirra.ai/mcp-server"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            . Need help?{' '}
            <a
              href="https://docs.cirra.ai/mcp-server/support"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact us for support
            </a>
          </p>
        </div>
        <div className="separator" />

        <Tabs
          activeKey={activeTab}
          items={tabs}
          onChange={(activeTabKey) => {
            setActiveTab(activeTabKey);
            navigate(`${location.pathname}${location.search}#${activeTabKey}`);
          }}
        />
      </div>
    </>
  );
};

export default McpScreen;
